import { z } from 'zod';
import { EmailPreviewSchema } from './common';

export const SenderSortBySchema = z.enum(['count', 'size', 'sender'])
export const SortDirectionSchema = z.enum(['asc', 'desc'])

export const GetSendersQuerySchema = z.object({
    limit: z.string().regex(/^\d+$/).transform(Number).optional().default(50),
    offset: z.string().regex(/^\d+$/).transform(Number).optional().default(0),
    search: z.string().trim().min(1).max(200).optional(),
    sortBy: SenderSortBySchema.optional().default('count'),
    sortDir: SortDirectionSchema.optional().default('desc'),
})

// Category stats: aggregate counts and sizes per category
export const GetCategoryStatsQuerySchema = z.object({
    limit: z.string().regex(/^\d+$/).transform(Number).optional().default(50),
    offset: z.string().regex(/^\d+$/).transform(Number).optional().default(0),
})

export const CategoryStatSchema = z.object({
    category: z.string(),
    count: z.number(),
    total_size_bytes: z.number(),
})

// Email previews query: either senderEmail or category must be provided
export const GetEmailsQuerySchema = z.object({
    senderEmail: z.string().optional(),
    category: z.string().optional(),
    limit: z.string().regex(/^\d+$/).transform(Number).optional().default(50),
    offset: z.string().regex(/^\d+$/).transform(Number).optional().default(0),
}).refine((data) => !!data.senderEmail || !!data.category, {
    message: 'Either senderEmail or category must be provided',
    path: ['senderEmail'],
})

// Get senders by category query
export const GetSendersByCategoryQuerySchema = z.object({
    category: z.string(),
    limit: z.string().regex(/^\d+$/).transform(Number).optional().default(50),
    offset: z.string().regex(/^\d+$/).transform(Number).optional().default(0),
})

export const SenderByCategorySchema = z.object({
    from_address: z.string(),
    email_count: z.number(),
    total_size_bytes: z.number(),
})

export const GetSendersByCategoryResponseSchema = z.object({
    senders: z.array(SenderByCategorySchema),
    total: z.number(),
    category: z.string(),
})

// Label analysis schemas
export const GetLabelAnalysisQuerySchema = z.object({
    limit: z.string().regex(/^\d+$/).transform(Number).optional().default(50),
    offset: z.string().regex(/^\d+$/).transform(Number).optional().default(0),
})

export const SenderLabelInfoSchema = z.object({
    sender_email: z.string(),
    has_mixed_labels: z.boolean(),
    unique_label_count: z.number(),
    email_count: z.number(),
    total_size_bytes: z.number(),
    sample_labels: z.array(z.string()),
})

export const GetLabelAnalysisResponseSchema = z.object({
    senders: z.array(SenderLabelInfoSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
})

// Get consistent label senders summary
export const GetConsistentLabelSendersQuerySchema = z.object({
    limit: z.string().regex(/^\d+$/).transform(Number).optional().default(50),
    offset: z.string().regex(/^\d+$/).transform(Number).optional().default(0),
})

export const ConsistentLabelSenderSchema = z.object({
    label: z.string(),
    sender_email: z.string(),
    email_count: z.number(),
    total_size_bytes: z.number(),
})

export const GetConsistentLabelSendersResponseSchema = z.object({
    senders: z.array(ConsistentLabelSenderSchema),
    summary: z.object({
        total_senders: z.number(),
        total_emails: z.number(),
        total_size_bytes: z.number(),
    }),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
})

// Sender summaries for /senders
export const SenderSummarySchema = z.object({
    sender_email: z.string(),
    sender_name: z.string(),
    junk_count: z.number(),
    total_size_bytes: z.number(),
})

export const GetSendersResponseSchema = z.object({
    senders: z.array(SenderSummarySchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
})

export type GetSendersResponse = z.infer<typeof GetSendersResponseSchema>
export type GetSendersByCategory = z.infer<typeof SenderByCategorySchema>
export type GetSendersByCategoryResponse = z.infer<typeof GetSendersByCategoryResponseSchema>

// Categories aggregate for /categories
export const GetCategoriesResponseSchema = z.object({
    categories: z.array(CategoryStatSchema),
})

// Email preview listing for /emails
export const GetEmailsResponseSchema = z.object({
    emails: z.array(EmailPreviewSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
})

